import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { MinionsDay, MinionsWeek } from '../report';
import { useMinionsI18n } from '../i18n';
import { niceScale } from '../format';
import { share } from '../derive';

/*
 * The design's drawings, plain SVG: unique visitors per day as bars with the
 * active players as a line over the whole campaign calendar (the running day in
 * red, a dashed rule where each Tuesday week starts), a bar per week, and the
 * consent donut. Bars carry tooltips; the daily chart also reads with the arrow
 * keys.
 */

/** Width of a box, following resizes (falls back where ResizeObserver is missing). */
function useWidth<T extends HTMLElement>(fallback = 800) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const obs = new ResizeObserver(([e]) => setWidth(Math.max(280, e.contentRect.width)));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, width] as const;
}

/** A column from the baseline up to `top` with a rounded data end; null when it has no height. */
function barPath(x: number, top: number, w: number, base: number, radius = 4): string | null {
  const h = base - top;
  if (h <= 0) return null;
  const r = Math.min(radius, w / 2, h);
  return `M${x},${base}V${top + r}Q${x},${top} ${x + r},${top}H${x + w - r}Q${x + w},${top} ${x + w},${top + r}V${base}Z`;
}

const H = 260;
const PAD = { l: 44, r: 8, t: 26, b: 26 };

export function DailyChart({ days }: { days: MinionsDay[] }) {
  const { t, f } = useMinionsI18n();
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const lastIdx = days.reduce((a, d, i) => (d.visitors != null ? i : a), -1);
  const top = Math.max(0, ...days.map((d) => Math.max(d.visitors ?? 0, d.activePlayers ?? 0))) * 1.08;
  const { step } = niceScale(top);
  // The axis ends at the next half step above the data, so the bars fill the height; ticks stay on whole steps.
  const max = Math.max(step / 2, Math.ceil(top / (step / 2)) * (step / 2));
  const base = H - PAD.b;
  const y = (v: number) => base - (v / max) * (base - PAD.t);
  const band = (width - PAD.l - PAD.r) / Math.max(1, days.length);
  const left = (i: number) => PAD.l + band * i;
  const bw = Math.max(2, band * 0.75);
  const ticks = Array.from({ length: Math.floor(max / step) + 1 }, (_v, i) => i * step);
  const every = width < 480 ? 7 : width < 800 ? 3 : 2;

  if (lastIdx < 0) return <p className="mn-note">{t.dailyEmpty}</p>;

  function onKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    setHover((h) => Math.min(lastIdx, Math.max(0, (h ?? lastIdx) + (e.key === 'ArrowRight' ? 1 : -1))));
  }
  const active = hover != null ? days[hover] : null;
  const current = days[hover ?? lastIdx];
  const valueText = (d: MinionsDay) => `${f.day(d.date, 'long')}: ${t.lgVisitors} ${f.int(d.visitors)}, ${t.lgDau} ${f.int(d.activePlayers)}`;
  const xMid = (i: number) => left(i) + band / 2;
  const line = days
    .map((d, i) => (d.activePlayers == null ? null : `${xMid(i)},${y(d.activePlayers)}`))
    .filter(Boolean)
    .join(' ');
  const last = days[lastIdx];

  return (
    <div
      ref={ref}
      className="mn-chart"
      role="slider"
      aria-label={t.dailyChartLabel}
      aria-valuemin={0}
      aria-valuemax={lastIdx}
      aria-valuenow={hover ?? lastIdx}
      aria-valuetext={current ? valueText(current) : undefined}
      tabIndex={0}
      onKeyDown={onKey}
      onFocus={() => setHover((h) => h ?? lastIdx)}
      onBlur={() => setHover(null)}
      onPointerMove={(e) => {
        const rect = ref.current?.getBoundingClientRect();
        const i = Math.floor((e.clientX - (rect?.left ?? 0) - PAD.l) / band);
        setHover(i < 0 || i > lastIdx ? null : i);
      }}
      onPointerLeave={() => setHover(null)}
    >
      <svg width={width} height={H} aria-hidden="true">
        {ticks.map((v) => (
          <g key={v}>
            <line x1={PAD.l} x2={width - PAD.r} y1={y(v)} y2={y(v)} className="mn-gridline" />
            <text x={PAD.l - 6} y={y(v) + 4} textAnchor="end">
              {v === 0 ? '0' : f.compact(v)}
            </text>
          </g>
        ))}
        {days.map((d, i) =>
          i % 7 === 0 ? (
            <g key={`w${d.date}`}>
              {i > 0 && <line x1={left(i)} x2={left(i)} y1={PAD.t - 14} y2={base} className="mn-week-rule" />}
              <text x={left(i) + 4} y={PAD.t - 8} className="mn-week-label">
                {width < 520 ? t.weekShort(i / 7 + 1) : t.week(i / 7 + 1)}
              </text>
            </g>
          ) : null,
        )}
        {active && <rect x={left(hover ?? 0)} y={PAD.t} width={band} height={base - PAD.t} className="mn-hover-band" />}
        {days.map((d, i) => {
          if (d.visitors == null) return null;
          const shape = barPath(left(i) + (band - bw) / 2, y(d.visitors), bw, base);
          return shape && <path key={d.date} d={shape} className={i === lastIdx ? 'mn-bar-hot' : 'mn-bar-soft'} />;
        })}
        {line && <polyline points={line} className="mn-line" />}
        {last.activePlayers != null && <circle cx={xMid(lastIdx)} cy={y(last.activePlayers)} r={3.5} className="mn-dot-line" />}
        {days.map((d, i) =>
          i % every === 0 ? (
            <text key={`x${d.date}`} x={xMid(i)} y={H - 8} textAnchor="middle">
              {i === 0 || d.date.endsWith('-01') ? f.day(d.date, 'dayMonth') : String(Number(d.date.slice(8, 10)))}
            </text>
          ) : null,
        )}
      </svg>
      {active && active.visitors != null && (
        <div className={`mn-tip${left(hover ?? 0) + 220 > width ? ' flip' : ''}`} style={{ left: left(hover ?? 0) + band }}>
          <div className="d">{f.day(active.date, 'long')}</div>
          <div>
            <i className="mn-sw soft" /> {t.lgVisitors}: <b>{f.int(active.visitors)}</b>
          </div>
          <div>
            <i className="mn-sw blue" /> {t.lgDau}: <b>{f.int(active.activePlayers)}</b>
          </div>
          <div>
            {t.cPlays}: <b>{f.int(active.plays)}</b>
          </div>
        </div>
      )}
    </div>
  );
}

/** One bar per campaign week; the running week red, weeks to come a flat ghost with "—". */
export function WeekBars({
  weeks,
  value,
  fmt,
  label,
}: {
  weeks: MinionsWeek[];
  value: (w: MinionsWeek) => number | null;
  fmt: (v: number) => string;
  label: string;
}) {
  const { t, f } = useMinionsI18n();
  const [ref, width] = useWidth<HTMLDivElement>(400);
  const h = 220;
  const pad = { l: 8, r: 8, t: 24, b: 26 };
  const max = Math.max(1, ...weeks.map((w) => value(w) ?? 0)) * 1.12;
  const band = (width - pad.l - pad.r) / Math.max(1, weeks.length);
  const bw = Math.min(64, band * 0.7);
  const base = h - pad.b;
  const y = (v: number) => base - (v / max) * (base - pad.t);
  return (
    <div ref={ref} className="mn-chart">
      <svg width={width} height={h} role="img" aria-label={label}>
        <line x1={pad.l} x2={width - pad.r} y1={base} y2={base} className="mn-axis" />
        {weeks.map((w, i) => {
          const v = value(w);
          const x = pad.l + band * i + (band - bw) / 2;
          const shape = v == null ? null : barPath(x, y(v), bw, base, 6);
          const name = `${t.week(w.week)}${w.status === 'live' ? ` (${t.inProgress})` : ''}`;
          return (
            <g key={w.week}>
              <title>{`${name}: ${v == null ? '—' : fmt(v)}`}</title>
              {shape ? (
                <path d={shape} className={w.status === 'live' ? 'mn-bar-hot' : 'mn-bar-blue'} />
              ) : (
                <rect x={x} y={base - 3} width={bw} height={3} rx={1.5} className="mn-bar-ghost" />
              )}
              <text x={x + bw / 2} y={v == null ? base - 8 : y(v) - 6} textAnchor="middle" className="mn-bar-value">
                {v == null ? '—' : width < 420 && v >= 10_000 ? f.compact(v) : fmt(v)}
              </text>
              <text x={x + bw / 2} y={h - 8} textAnchor="middle">
                {width < 360 ? t.weekShort(w.week) : t.week(w.week)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/** E-mail and SMS consents as a ring; the centre reads the SMS share, as the design draws it. */
export function ConsentDonut({ email, sms }: { email: number; sms: number }) {
  const { t, f } = useMinionsI18n();
  const S = 150;
  const radius = 56;
  const ring = 2 * Math.PI * radius;
  const total = email + sms;
  const parts = [
    { key: 'email', label: t.mcEmail, value: email, cls: 'mn-arc-blue' },
    { key: 'sms', label: t.mcSms, value: sms, cls: 'mn-arc-yellow' },
  ].filter((p) => p.value > 0);
  const gap = parts.length > 1 ? 2 : 0;
  let offset = 0;
  return (
    <svg className="mn-donut" width={S} height={S} viewBox={`0 0 ${S} ${S}`} role="img" aria-label={t.mcDonutLabel}>
      <g transform={`rotate(-90 ${S / 2} ${S / 2})`}>
        <circle cx={S / 2} cy={S / 2} r={radius} className="mn-ring-empty" strokeWidth={22} fill="none" />
        {total > 0 &&
          parts.map((p) => {
            const len = (p.value / total) * ring;
            const dash = Math.max(0, len - gap);
            const el = (
              <circle
                key={p.key}
                cx={S / 2}
                cy={S / 2}
                r={radius}
                fill="none"
                className={p.cls}
                strokeWidth={22}
                strokeDasharray={`${dash} ${ring - dash}`}
                strokeDashoffset={-offset}
              >
                <title>{`${p.label}: ${f.int(p.value)} · ${f.pct(share(p.value, total))}`}</title>
              </circle>
            );
            offset += len;
            return el;
          })}
      </g>
      <text x={S / 2} y={S / 2 + 5} textAnchor="middle" className="mn-donut-v">
        {f.pct(share(sms, total))}
      </text>
      <text x={S / 2} y={S / 2 + 21} textAnchor="middle" className="mn-donut-l">
        {t.mcSms}
      </text>
    </svg>
  );
}
